/**
 * LegacyFilterBar — boolean-prop-driven filter rendering (pre-Phase B).
 * Extracted from FilterBar.jsx to keep the main file under 300 lines.
 * Handles both mobile (collapsible panel) and desktop (inline) layouts.
 */
import { useState } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { useT } from '../../../lib/i18n/I18nContext';
import useBreakpoint from '../../../hooks/useBreakpoint';
import { departmentLabel } from '../../../lib/constants/departments';
import SearchInput from './SearchInput';
import ClearButton from './ClearButton';
import { selectStyle } from './filterStyles';

const GROUP_OPTIONS = [
  { value: 'project', labelKey: 'filter.groupProject' },
  { value: 'assignee', labelKey: 'filter.groupAssignee' },
  { value: 'department', labelKey: 'filter.groupDepartment' },
  { value: 'stage', labelKey: 'filter.groupStage' },
];

// Schema options can be strings or {id, name, order} objects — normalize to string
const optName = (o) => (typeof o === 'string' ? o : o?.name || '');

export default function LegacyFilterBar({
  filters,
  onFilterChange,
  onClearAll,
  projects,
  people,
  bundles,
  statuses,
  statusLabels,
  bundleStageLabels,
  stages,
  departments,
  projectTypes,
  showGroupBy,
  showBundle,
  showBundleStage,
  showDepartment,
  showSearch,
  showProjectType,
  showStage,
  showVessel,
  vesselOptions = [],
  hasActiveFilter,
  activeFilterCount,
  hiddenBundleCount = 0,
}) {
  const { t, locale } = useT();
  const { isMobile } = useBreakpoint();
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Build filter elements with the given base style
  const buildFilterElements = (baseStyle, { skipSearch = false } = {}) => {
    const elements = [];

    // Status — always shown
    elements.push(
      <select key="status" value={filters.status || ''} onChange={(e) => onFilterChange && onFilterChange('status', e.target.value)} style={baseStyle}>
        <option value="">{t('filter.allStatuses')}</option>
        {statuses.map((s) => {
          const slug = typeof s === 'string' ? s : (s?.id || optName(s));
          const label = statusLabels[slug] || slug;
          return <option key={slug} value={slug}>{label}</option>;
        })}
      </select>
    );

    // Bundle stage — conditional
    if (showBundleStage) {
      elements.push(
        <select key="bundleStage" value={filters.bundleStage || ''} onChange={(e) => onFilterChange && onFilterChange('bundleStage', e.target.value)} style={baseStyle}>
          <option value="">{t('filter.allStages')}</option>
          {bundleStageLabels.map((label) => <option key={label} value={label}>{label}</option>)}
        </select>
      );
    }

    // Assignee — always shown
    elements.push(
      <select key="assignee" value={filters.assignee || ''} onChange={(e) => onFilterChange && onFilterChange('assignee', e.target.value)} style={baseStyle}>
        <option value="">{t('filter.allPeople')}</option>
        {people.map((p) => <option key={p.id} value={p.id}>{p.display_name || p.id}</option>)}
      </select>
    );

    // Project — always shown
    elements.push(
      <select key="project" value={filters.project || ''} onChange={(e) => onFilterChange && onFilterChange('project', e.target.value)} style={baseStyle}>
        <option value="">{t('filter.allProjects')}</option>
        {projects.map((p) => <option key={p.id} value={p.id}>{p.project_name || p.id}</option>)}
      </select>
    );

    // Bundle — conditional
    if (showBundle) {
      elements.push(
        <select key="bundle" value={filters.bundle || ''} onChange={(e) => onFilterChange && onFilterChange('bundle', e.target.value)} style={baseStyle}>
          <option value="">{t('filter.allBundles')}</option>
          {bundles.map((b) => <option key={b.id} value={b.id}>{b.title || b.name || b.id}</option>)}
        </select>
      );
    }

    // Vessel — conditional (TICKET-714: all projects done to one boat)
    if (showVessel && vesselOptions.length > 0) {
      elements.push(
        <select key="vessel" data-testid="filter-vessel" value={filters.vessel || ''} onChange={(e) => onFilterChange && onFilterChange('vessel', e.target.value)} style={baseStyle}>
          <option value="">{t('filter.allVessels', 'All boats')}</option>
          {vesselOptions.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
      );
    }

    // Department — conditional
    if (showDepartment && departments.length > 0) {
      elements.push(
        <select key="department" value={filters.department || ''} onChange={(e) => onFilterChange && onFilterChange('department', e.target.value)} style={baseStyle}>
          <option value="">{t('filter.allDepartments')}</option>
          {departments.map((d) => {
            const slug = typeof d === 'string' ? d : (d?.id || optName(d));
            return <option key={slug} value={slug}>{departmentLabel(slug, locale)}</option>;
          })}
        </select>
      );
    }

    // Project Type — conditional
    if (showProjectType && projectTypes.length > 0) {
      elements.push(
        <select
          key="projectType"
          value={filters.projectType || ''}
          onChange={(e) => {
            if (onFilterChange) {
              onFilterChange('projectType', e.target.value);
              if (filters.stage) onFilterChange('stage', '');
            }
          }}
          style={baseStyle}
        >
          <option value="">{t('filter.allTypes')}</option>
          {projectTypes.map((pt) => {
            const name = optName(pt);
            return <option key={pt?.id || name} value={pt?.id || name}>{name}</option>;
          })}
        </select>
      );
    }

    // Stage — conditional
    if (showStage && stages.length > 0) {
      elements.push(
        <select key="stage" value={filters.stage || ''} onChange={(e) => onFilterChange && onFilterChange('stage', e.target.value)} style={baseStyle}>
          <option value="">{t('filter.allStages')}</option>
          {stages.map((s) => {
            const name = optName(s);
            return <option key={name} value={name}>{name}</option>;
          })}
        </select>
      );
    }

    // Group By — conditional
    if (showGroupBy) {
      if (!isMobile) {
        elements.push(<div key="divider" style={{ width: 1, height: 20, background: 'var(--bdr)', flexShrink: 0 }} />);
      }
      elements.push(
        <select key="groupBy" value={filters.groupBy || 'project'} onChange={(e) => onFilterChange && onFilterChange('groupBy', e.target.value)} style={{ ...baseStyle, fontWeight: 500 }}>
          {GROUP_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>)}
        </select>
      );
    }

    // Search — conditional. On mobile it is hoisted to an always-visible bar
    // above the Filters toggle (skipSearch), so don't also render it inside the
    // collapsed panel (TICKET-075-UI — CEO 2026-06-10).
    if (showSearch && !skipSearch) {
      elements.push(
        <SearchInput key="search" value={filters.search || ''} onChange={(v) => onFilterChange && onFilterChange('search', v)} fullWidth={isMobile} />
      );
    }

    // Phase B1: hidden-by-filter bundle count hint
    if (hiddenBundleCount > 0) {
      elements.push(
        <span
          key="hiddenBundles"
          data-testid="filter-hidden-bundles"
          style={{ fontSize: 12, color: 'var(--tm)', fontStyle: 'italic', whiteSpace: 'nowrap' }}
        >
          · {hiddenBundleCount} bundle{hiddenBundleCount > 1 ? 's' : ''} hidden by filter
        </span>
      );
    }

    // Clear all
    if (hasActiveFilter && onClearAll) {
      elements.push(<ClearButton key="clear" onClick={onClearAll} fullWidth={isMobile} />);
    }

    return elements;
  };

  // Mobile: chip toggle + collapsible panel
  if (isMobile) {
    const mobileStyle = { ...selectStyle, width: '100%', flex: 'unset' };
    return (
      <div>
        {/* TICKET-075-UI — keep search ALWAYS visible above the Filters toggle
            instead of buried in the collapsed panel (CEO 2026-06-10: "need a
            small search bar within projects"). The other filters stay collapsed. */}
        {showSearch && (
          <div style={{ marginBottom: 8 }} data-testid="filterbar-mobile-search">
            <SearchInput
              value={filters.search || ''}
              onChange={(v) => onFilterChange && onFilterChange('search', v)}
              fullWidth
            />
          </div>
        )}
        <button
          onClick={() => setFiltersOpen((prev) => !prev)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 16px',
            borderRadius: 99,
            background: hasActiveFilter ? 'var(--navy)' : 'var(--glass)',
            color: hasActiveFilter ? 'var(--card)' : 'var(--t2)',
            fontSize: 13,
            fontWeight: 600,
            minHeight: 44,
            border: '1px solid var(--bdr)',
            cursor: 'pointer',
          }}
        >
          <SlidersHorizontal size={14} strokeWidth={1.8} />
          {activeFilterCount > 0
            ? `${t('filter.filters')} (${activeFilterCount})`
            : t('filter.filters')}
        </button>
        {filtersOpen && (
          <div
            style={{
              marginTop: 8,
              padding: 12,
              borderRadius: 12,
              background: 'var(--glass)',
              border: '1px solid var(--bdr)',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            {buildFilterElements(mobileStyle, { skipSearch: true })}
          </div>
        )}
      </div>
    );
  }

  // Desktop / tablet: inline layout
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 12px',
        background: 'var(--glass)',
        borderRadius: 12,
        border: '1px solid var(--bdr)',
        flexWrap: 'wrap',
      }}
    >
      {buildFilterElements(selectStyle)}
    </div>
  );
}
